import { useState, useEffect, useCallback } from "react";
import { Calculator, Loader2, Lock, CheckCircle2, AlertCircle } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { inr, currentMonthIST } from "@/lib/format";
import { PageHeader, Loading, StatusBadge } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";

function Line({ label, value, strong, tone }) {
  return (
    <div className={`flex justify-between py-2 border-b border-slate-100 last:border-0 ${strong ? "font-semibold" : ""}`}>
      <span className={`text-sm ${strong ? "text-slate-800" : "text-slate-500"}`}>{label}</span>
      <span className={`text-sm tabular-nums ${tone || "text-slate-800"}`}>{value}</span>
    </div>
  );
}

export default function Settlements() {
  const [physios, setPhysios] = useState([]);
  const [physioId, setPhysioId] = useState("");
  const [month, setMonth] = useState(currentMonthIST());
  const [basis, setBasis] = useState("service_charges"); // service_charges | actual_collections
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [payAmt, setPayAmt] = useState("");

  useEffect(() => {
    api.get("/users/physiotherapists").then((r) => {
      setPhysios(r.data);
      if (r.data[0]) setPhysioId(r.data[0].id);
    });
  }, []);

  const compute = useCallback(async () => {
    if (!physioId) return;
    setLoading(true);
    try {
      const { data } = await api.get("/settlements/compute", {
        params: { physio_id: physioId, month, basis }
      });
      setData(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [physioId, month, basis]);

  useEffect(() => {
    compute();
  }, [compute]);

  const existing = data?.existing;
  const locked = existing?.locked;

  const generate = async () => {
    try {
      await api.post("/settlements/generate", {
        physio_id: physioId,
        month,
        basis,
      });
      toast.success("Settlement generated (draft)");
      compute();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const approve = async () => {
    try {
      await api.post(`/settlements/${existing.id}/approve`);
      toast.success("Settlement approved & locked");
      compute();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const markPaid = async () => {
    if (!payAmt || Number(payAmt) <= 0) {
      toast.error("Enter amount");
      return;
    }
    try {
      await api.post(`/settlements/${existing.id}/mark-paid`, { amount: Number(payAmt) });
      toast.success("Payment recorded");
      setPayAmt("");
      compute();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Physiotherapist Monthly Settlement"
        subtitle="Revenue share, travel/petrol expenses, and transparent payout calculations"
        testid="settlements-page"
      />

      <Card className="p-4">
        <div className="grid sm:grid-cols-4 gap-3">
          <div className="space-y-1.5">
            <Label>Physiotherapist</Label>
            <Select value={physioId} onValueChange={setPhysioId}>
              <SelectTrigger data-testid="settle-physio"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent>
                {physios.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Month</Label>
            <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="settle-month" />
          </div>

          <div className="space-y-1.5">
            <Label>Settlement Basis</Label>
            <Select value={basis} onValueChange={setBasis}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="service_charges">Completed Service Charges (Default)</SelectItem>
                <SelectItem value="actual_collections">Actual Collections</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-end">
            <Button onClick={compute} variant="outline" className="w-full">
              <Calculator className="h-4 w-4 mr-1" /> Recompute
            </Button>
          </div>
        </div>
      </Card>

      {loading ? (
        <Loading label="Computing settlement arithmetic..." />
      ) : data && (
        <div className="grid lg:grid-cols-2 gap-4">
          <Card className="p-5" data-testid="settlement-breakdown">
            <div className="flex items-center justify-between mb-3">
              <p className="font-display font-bold text-slate-800 text-base">{data.physio_name} · {month}</p>
              {existing && <StatusBadge status={existing.status} />}
            </div>

            <Line label="Completed Visits" value={data.total_visits} />
            <Line label="Gross Revenue Basis" value={inr(data.gross_revenue)} strong />
            <Line label={`Owner Share (${data.owner_pct}%)`} value={inr(data.owner_share)} tone="text-slate-500" />
            <Line label={`Physio Share (${data.physio_pct}%)`} value={inr(data.physio_share)} tone="text-bk-green font-semibold" strong />
            <Line label="Approved Petrol / Travel" value={inr(data.approved_petrol)} />
            <Line label={`Owner Petrol Share (${data.petrol_owner_pct}%)`} value={inr(data.owner_petrol_share)} tone="text-slate-500" />
            <Line label={`Physio Petrol Share (${data.petrol_physio_pct}%)`} value={`- ${inr(data.physio_petrol_share)}`} tone="text-red-600 font-medium" />
            <Line label="Physio Net (Share − Petrol Share)" value={inr(data.physio_net)} strong tone="text-bk-blue font-bold" />
            <Line label="Already Paid" value={`- ${inr(data.already_paid)}`} tone="text-slate-500" />

            <div className="flex justify-between py-3 mt-2 border-t-2 border-slate-200">
              <span className="font-display font-bold text-slate-900 text-base">Final Settlement Payable</span>
              <span className="font-display font-extrabold text-xl text-bk-green tabular-nums">{inr(data.final_balance)}</span>
            </div>
          </Card>

          <Card className="p-5 space-y-4">
            <div>
              <p className="font-display font-bold text-slate-800 text-base mb-2">Deposit & Reconciliation</p>
              <div className="p-3 bg-slate-50 border rounded-lg space-y-2 text-xs">
                <div className="flex justify-between">
                  <span className="text-slate-500">Collected by Physiotherapist:</span>
                  <span className="font-semibold text-slate-800">{inr(data.collected_by_physio)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Verified Deposits Submitted:</span>
                  <span className="font-semibold text-emerald-700">{inr(data.verified_deposits)}</span>
                </div>
                <div className="flex justify-between border-t pt-1.5 font-bold">
                  <span className="text-red-700">Separate Pending Deposit Liability:</span>
                  <span className="text-red-700">{inr(data.pending_deposit)}</span>
                </div>
              </div>
              <p className="text-[11px] text-slate-400 mt-1 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                Pending deposits are tracked in Deposit Liabilities and are not silently deducted from salary.
              </p>
            </div>

            <div className="space-y-2 pt-2 border-t">
              {!existing || existing.status === "draft" ? (
                <>
                  <Button onClick={generate} data-testid="generate-settlement" className="w-full bg-bk-blue hover:bg-blue-800 text-white">
                    {existing ? "Regenerate Draft Settlement" : "Generate Settlement"}
                  </Button>
                  {existing && (
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button className="w-full bg-bk-green hover:bg-emerald-700 text-white" data-testid="approve-settlement">
                          <CheckCircle2 className="h-4 w-4 mr-1" /> Approve & Lock Settlement
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Approve & Lock Settlement?</AlertDialogTitle>
                          <AlertDialogDescription>
                            Once approved, this settlement is locked and ready for payment disbursement.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction onClick={approve} className="bg-bk-green hover:bg-emerald-700 text-white">
                            Confirm Approval
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  )}
                </>
              ) : (
                <div className="space-y-3">
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-800 flex items-center gap-2">
                    <Lock className="h-4 w-4 text-emerald-600 shrink-0" />
                    <span>Settlement approved and locked. Status: <strong>{existing.status}</strong></span>
                  </div>

                  {existing.status !== "paid" && (
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder={`Pay (${inr(data.final_balance)})`}
                        value={payAmt}
                        onChange={(e) => setPayAmt(e.target.value)}
                        className="h-9 text-xs"
                      />
                      <Button onClick={markPaid} className="bg-bk-green hover:bg-emerald-700 text-white h-9 text-xs">
                        Record Payment
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
